import { useState, useRef, useCallback, useEffect } from "react";
import { detectPaperGuide, type GuideDetectParams } from "./guideDetection";
import {
  AUTO_CAPTURE_TIMEOUT_MS,
  CAPTURE_BBOX_PADDING,
  DETECTION_CONFIDENCE_MS,
  DETECTION_HOLD_MS,
  EDGE_DETECTION_FRAME_SKIP,
  PAPER_GUIDE_PARAMS,
  VIDEO_CONSTRAINTS,
} from "./constants";

export type CameraStatus =
  | "idle"
  | "requesting"
  | "active"
  | "detected"
  | "capturing"
  | "captured"
  | "closing"
  | "denied"
  | "error";

export type { GuideDetectParams };

/** All params exposed by the paper camera — detector tunings + capture meta. */
export interface PaperCameraParams extends GuideDetectParams {
  detectionHoldMs: number;
  /** Extra margin (fraction of bbox) added on each side when cropping the capture. */
  captureBboxPadding: number;
  /** When false, the 5s fallback auto-capture countdown never starts. */
  safetyTimerEnabled: boolean;
}

interface UsePaperCameraOptions {
  debugMode?: boolean;
}

export interface DetectionDebugValues {
  score: number;
  frameFill: number;
  bboxFill: number;
  edgeContrast: number;
}

interface UsePaperCameraReturn {
  status: CameraStatus;
  countdown: number;
  /** Seconds left on the safety timer that force-captures even without detection. */
  autoCaptureCountdown: number;
  /** Live detection score [0–1] — use in debug UI. */
  detectionScore: number;
  /** Intermediate values that make up the score — for debug UI. */
  debugValues: DetectionDebugValues;
  /** Data URL of the last captured image — available after capture. */
  lastCapturedImage: string | null;
  videoRef: React.RefObject<HTMLVideoElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  edgeCanvasRef: React.RefObject<HTMLCanvasElement>;
  params: PaperCameraParams;
  setParams: React.Dispatch<React.SetStateAction<PaperCameraParams>>;
  open: () => void;
  close: () => void;
  captureManually: () => void;
}

const SLIDE_OUT_MS = 350;

const defaultParams: PaperCameraParams = {
  ...PAPER_GUIDE_PARAMS,
  detectionHoldMs: DETECTION_HOLD_MS,
  captureBboxPadding: CAPTURE_BBOX_PADDING,
  safetyTimerEnabled: true,
};

export function usePaperCamera({
  debugMode = false,
}: UsePaperCameraOptions = {}): UsePaperCameraReturn {
  const [status, setStatus] = useState<CameraStatus>("idle");
  const [countdown, setCountdown] = useState(0);
  const [autoCaptureCountdown, setAutoCaptureCountdown] = useState(0);
  const [detectionScore, setDetectionScore] = useState(0);
  const [debugValues, setDebugValues] = useState<DetectionDebugValues>({
    score: 0,
    frameFill: 0,
    bboxFill: 0,
    edgeContrast: 0,
  });
  const [lastCapturedImage, setLastCapturedImage] = useState<string | null>(
    null,
  );
  const [params, setParams] = useState<PaperCameraParams>(defaultParams);

  const paramsRef = useRef(params);
  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const edgeCanvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const frameSkipRef = useRef(0);
  const detectedAtRef = useRef<number | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const autoCaptureIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const autoCaptureDeadlineRef = useRef<number | null>(null);
  const debugModeRef = useRef(debugMode);
  useEffect(() => {
    debugModeRef.current = debugMode;
  }, [debugMode]);

  // Timestamp when score first exceeded threshold in the current streak.
  const confidenceAtRef = useRef<number | null>(null);
  // Consecutive failed-detection frames. We tolerate short dropouts so a
  // single-frame flicker (hand waving past, autoexposure blip) doesn't wipe
  // out accumulated confidence.
  const failStreakRef = useRef(0);
  const MAX_FAIL_STREAK = 4;

  // Stable ref to the detection loop so capture() can restart it without a circular dep.
  const runDetectionLoopRef = useRef<() => void>(() => {});
  // Stable ref to capture() so the safety-timer tick can fire it without a circular dep.
  const captureRef = useRef<(manual?: boolean) => void>(() => {});
  // Latest normalised bbox from detection — used by capture() to crop.
  const bboxRef = useRef<{ x: number; y: number; w: number; h: number } | null>(
    null,
  );

  const stopAutoCaptureTimer = useCallback(() => {
    if (autoCaptureIntervalRef.current !== null) {
      clearInterval(autoCaptureIntervalRef.current);
      autoCaptureIntervalRef.current = null;
    }
    autoCaptureDeadlineRef.current = null;
    setAutoCaptureCountdown(0);
  }, []);

  const startAutoCaptureTimer = useCallback(() => {
    // Honor the debug toggle — skip arming the safety timer when disabled.
    if (!paramsRef.current.safetyTimerEnabled) return;
    // Defensively clear any prior interval (e.g. leaked across hot reloads)
    // before starting a new one. Otherwise an orphaned interval could keep
    // ticking and preempt the new deadline.
    if (autoCaptureIntervalRef.current !== null) {
      clearInterval(autoCaptureIntervalRef.current);
      autoCaptureIntervalRef.current = null;
    }
    const deadline = Date.now() + AUTO_CAPTURE_TIMEOUT_MS;
    autoCaptureDeadlineRef.current = deadline;
    setAutoCaptureCountdown(Math.ceil(AUTO_CAPTURE_TIMEOUT_MS / 1000));

    autoCaptureIntervalRef.current = setInterval(() => {
      const remaining = deadline - Date.now();
      if (remaining <= 0) {
        if (autoCaptureIntervalRef.current !== null) {
          clearInterval(autoCaptureIntervalRef.current);
          autoCaptureIntervalRef.current = null;
        }
        autoCaptureDeadlineRef.current = null;
        setAutoCaptureCountdown(0);
        // Use the last bbox detection saw (if any) — it's usually a better
        // crop than full-frame even when the gates didn't pass. capture(false)
        // falls back to full-frame automatically when no bbox is cached.
        captureRef.current(false);
      } else {
        setAutoCaptureCountdown(Math.ceil(remaining / 1000));
      }
    }, 200);
  }, []);

  const stopStream = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (countdownIntervalRef.current !== null) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    stopAutoCaptureTimer();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    detectedAtRef.current = null;
    confidenceAtRef.current = null;
    failStreakRef.current = 0;
  }, [stopAutoCaptureTimer]);

  const close = useCallback(() => {
    stopStream();
    setCountdown(0);
    setDetectionScore(0);
    setStatus("closing");
    setTimeout(() => setStatus("idle"), SLIDE_OUT_MS);
  }, [stopStream]);

  const capture = useCallback(
    async (manual = false) => {
      const video = videoRef.current;
      if (!video) return;

      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (countdownIntervalRef.current !== null) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      stopAutoCaptureTimer();

      setStatus("capturing");

      // Use a fresh offscreen canvas — the shared canvasRef is flagged
      // `willReadFrequently` for the detection loop, which on some browsers
      // forces a CPU-backed context that can subtly degrade drawImage output.
      // Encode at JPEG 1.0 (not PNG) so downstream consumers — which strip the
      // `data:image/…` prefix and pass raw base64 — keep getting JPEG bytes.
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      const bbox = manual ? null : bboxRef.current;
      const pad = paramsRef.current.captureBboxPadding;
      const cropX = bbox ? Math.max(0, (bbox.x - pad) * vw) : 0;
      const cropY = bbox ? Math.max(0, (bbox.y - pad) * vh) : 0;
      const cropW = bbox ? Math.min(vw - cropX, (bbox.w + pad * 2) * vw) : vw;
      const cropH = bbox ? Math.min(vh - cropY, (bbox.h + pad * 2) * vh) : vh;
      const captureCanvas = document.createElement("canvas");
      captureCanvas.width = cropW;
      captureCanvas.height = cropH;
      const ctx = captureCanvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
      const dataUrl = captureCanvas.toDataURL("image/jpeg", 1.0);

      setLastCapturedImage(dataUrl);

      detectedAtRef.current = null;
      confidenceAtRef.current = null;
      setCountdown(0);
      setTimeout(() => {
        setStatus("active");
        frameSkipRef.current = 0;
        rafRef.current = requestAnimationFrame(runDetectionLoopRef.current);
      }, 600);
    },
    [stopAutoCaptureTimer],
  );

  const startCountdownTicker = useCallback((holdMs: number) => {
    if (countdownIntervalRef.current !== null) return;
    const endTime = (detectedAtRef.current ?? Date.now()) + holdMs;
    setCountdown(Math.ceil((endTime - Date.now()) / 1000));

    countdownIntervalRef.current = setInterval(() => {
      const remaining = endTime - Date.now();
      if (remaining <= 0) {
        if (countdownIntervalRef.current !== null) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        setCountdown(0);
      } else {
        setCountdown(Math.ceil(remaining / 1000));
      }
    }, 200);
  }, []);

  const stopCountdownTicker = useCallback(() => {
    if (countdownIntervalRef.current !== null) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    detectedAtRef.current = null;
    setCountdown(0);
  }, []);

  const runDetectionLoop = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(runDetectionLoop);
      return;
    }

    frameSkipRef.current++;
    if (frameSkipRef.current < EDGE_DETECTION_FRAME_SKIP) {
      rafRef.current = requestAnimationFrame(runDetectionLoop);
      return;
    }
    frameSkipRef.current = 0;

    const current = paramsRef.current;
    const { detectionHoldMs } = current;

    const DW = 160;
    const DH = Math.round((video.videoHeight / video.videoWidth) * DW);
    canvas.width = DW;
    canvas.height = DH;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      rafRef.current = requestAnimationFrame(runDetectionLoop);
      return;
    }

    ctx.drawImage(video, 0, 0, DW, DH);
    const imageData = ctx.getImageData(0, 0, DW, DH);
    const result = detectPaperGuide(imageData, current);

    if (result.bbox.w > 0) bboxRef.current = result.bbox;

    if (debugModeRef.current) {
      setDetectionScore(result.score);
      setDebugValues({
        score: result.score,
        frameFill: result.frameFill,
        bboxFill: result.bboxFill,
        edgeContrast: result.edgeContrast,
      });
      const ec = edgeCanvasRef.current;
      if (ec) {
        ec.width = DW;
        ec.height = DH;
        ec.getContext("2d")?.putImageData(result.debugImageData, 0, 0);
      }
    }

    const now = Date.now();
    if (result.detected) {
      failStreakRef.current = 0;
      if (confidenceAtRef.current === null) {
        confidenceAtRef.current = now;
      } else if (
        detectedAtRef.current === null &&
        now - confidenceAtRef.current >= DETECTION_CONFIDENCE_MS
      ) {
        detectedAtRef.current = now;
        setStatus("detected");
        startCountdownTicker(detectionHoldMs);
      } else if (
        detectedAtRef.current !== null &&
        now - detectedAtRef.current >= detectionHoldMs
      ) {
        capture();
        return;
      }
    } else {
      failStreakRef.current++;
      // Tolerate brief dropouts — don't nuke the accumulated confidence or
      // kick out of the "detected" hold period on a single bad frame.
      if (failStreakRef.current > MAX_FAIL_STREAK) {
        if (detectedAtRef.current !== null) {
          stopCountdownTicker();
          setStatus("active");
        }
        confidenceAtRef.current = null;
      }
    }

    rafRef.current = requestAnimationFrame(runDetectionLoop);
  }, [capture, startCountdownTicker, stopCountdownTicker]);

  // Keep the ref current so capture() can restart the loop without a circular dep.
  useEffect(() => {
    runDetectionLoopRef.current = runDetectionLoop;
  }, [runDetectionLoop]);

  // Mirror capture() into a ref so the safety timer can fire it without
  // forming a circular dependency between the timer setup and capture.
  useEffect(() => {
    captureRef.current = capture;
  }, [capture]);

  const open = useCallback(async () => {
    if (status === "requesting" || status === "active" || status === "detected")
      return;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: VIDEO_CONSTRAINTS,
      });
    } catch (err: unknown) {
      const name = err instanceof Error ? err.name : "";
      const isDenied =
        name === "NotAllowedError" ||
        name === "PermissionDeniedError" ||
        name === "SecurityError";
      setStatus(isDenied ? "denied" : "error");
      if (!isDenied) setTimeout(() => setStatus("idle"), 3000);
      return;
    }

    streamRef.current = stream;

    const video = videoRef.current;
    if (!video) {
      stream.getTracks().forEach((t) => t.stop());
      setStatus("error");
      setTimeout(() => setStatus("idle"), 3000);
      return;
    }

    video.srcObject = stream;

    // Flip to "active" and kick off the safety timer BEFORE waiting on
    // video.play() — on some browsers play() can take several hundred ms,
    // during which the user would otherwise see nothing happening.
    setStatus("active");
    startAutoCaptureTimer();

    try {
      await video.play();
    } catch {
      /* non-fatal */
    }

    frameSkipRef.current = 0;
    rafRef.current = requestAnimationFrame(runDetectionLoop);
  }, [status, runDetectionLoop, startAutoCaptureTimer]);

  useEffect(() => () => stopStream(), [stopStream]);

  // React to mid-session toggles of the debug flag: if the user flips the
  // safety timer off while it's ticking, stop it immediately. If they flip
  // it back on, we don't auto-arm — it'll start the next time the camera
  // opens. That's simpler and avoids a surprise capture.
  useEffect(() => {
    if (!params.safetyTimerEnabled) {
      stopAutoCaptureTimer();
    }
  }, [params.safetyTimerEnabled, stopAutoCaptureTimer]);

  return {
    status,
    countdown,
    autoCaptureCountdown,
    detectionScore,
    debugValues,
    lastCapturedImage,
    videoRef,
    canvasRef,
    edgeCanvasRef,
    params,
    setParams,
    open,
    close,
    captureManually: () => capture(true),
  };
}
